import React, { useState, useEffect, useRef } from 'react';
import { opportunitySkin } from '../lib/opportunitySkin';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { opportunitiesAPI } from '../lib/api';
import RichTextEditor from '../components/RichTextEditor';
import ImageAdjust from '../components/ImageAdjust';
import { RepeatableGroup, FaqManager, TeamPicker } from '../components/OpportunitySubEditors';
import { toast } from 'sonner';
import { Loader2, Upload, X, FileText, ExternalLink, ChevronLeft, Trash2, Send, Check } from 'lucide-react';
import { useSettings } from '../App';
import { StatusChip } from '../components/ModernOpportunitiesDevelopment';
import { FundItEditor } from '../components/FundIt';

const PB_FONT = "'Plus Jakarta Sans', 'Inter', sans-serif";
const inputCls = 'h-11 w-full px-4 text-sm bg-white border border-slate-200 rounded-sm ' +
  'text-slate-700 placeholder:text-slate-400 outline-none transition-[border-color,box-shadow] duration-150 ' +
  'focus:border-[var(--color-accent,#0D9488)] focus:shadow-[0_0_0_3px_rgba(13,148,136,0.12)]';
const TIMER_OPTIONS = [6, 12, 24, 36, 72];
const DATE_FIELDS = [
  ['launch', 'Launch Date'], ['funding_end', 'Funding End Date'],
  ['project', 'Project Date'], ['reporting', 'Reporting Date'],
  ['distribution', 'Distribution Date'],
];
const SOCIAL_FIELDS = ['facebook', 'twitter', 'linkedin', 'google', 'instagram'];

const EMPTY = {
  name: '', type_id: '', country_id: '', state_id: '', city_id: '',
  contact_email: '', video_url: '',
  dates: { launch: '', funding_end: '', project: '', reporting: '', distribution: '' },
  total_amount: '', minimum_investment_amount: '',
  summary: '', description: '',
  socials: { facebook: '', twitter: '', linkedin: '', google: '', instagram: '' },
  show_mode: 'all', timer_hours: '',
  images: [null, null, null, null, null], pfs_url: null,
  files: [], backers: [], services: [], benefits: [], updates: [], faq: [], team: [],
  investment: {},
  status: 'draft',
};
const SUB_GROUPS = ['files', 'backers', 'services', 'benefits', 'updates'];

// «Moderno» skin (s141, approved "Section Rail"): same form and logic, flat
// sections with ids for the rail, labels above fields, graphite action bar.
const ModernSkin = React.createContext(false);
const NAV_H = 80; // fixed Moderno navbar (Navbar.js h-20)
const FAINT = 'rgba(20,26,58,.5)';
const MOE_CSS = `
.moe .grid>*{min-width:0}
.moe [data-testid^="group-"],.moe [data-testid="faq-manager"],.moe [data-testid="team-picker"]{background:none!important;border:0!important;padding:0!important;border-radius:0!important}
.moe section h2{font-family:var(--op-head,'Playfair Display',Georgia,serif);font-size:1.35rem;font-weight:600;color:var(--color-heading,#1a2332)}
.moe input:not([type=radio]):not([type=checkbox]):not([type=file]),.moe select{height:46px;border-color:rgba(20,26,58,.16);border-radius:3px;font-size:14.5px;color:var(--color-heading,#1a2332)}
.moe input:not([type=radio]):not([type=checkbox]):focus,.moe select:focus{border-color:var(--color-heading,#1a2332)!important;box-shadow:0 0 0 3px rgba(0,12,82,.1)!important}
.moe .uppercase.tracking-wide.text-xs{text-transform:none;letter-spacing:0;font-size:12.5px;color:var(--color-heading,#1a2332)}
.moe input[type=radio]{accent-color:var(--color-heading,#1a2332)}
.moe .rounded-sm{border-radius:3px}
`;

function Section({ id, title, hint, children }) {
  const modern = React.useContext(ModernSkin);
  if (modern) {
    return (
      <section id={id}>
        <h2 className="mb-1">{title}</h2>
        {hint ? <p className="text-[13.5px] mb-5" style={{ color: FAINT }}>{hint}</p> : <div className="mb-4" />}
        {children}
      </section>
    );
  }
  return (
    <section className="bg-white rounded-sm border border-slate-100 p-6 md:p-8">
      <h2 className="text-lg font-bold mb-1" style={{ color: 'var(--color-heading, #1a2332)', fontFamily: 'Playfair Display, serif' }}>{title}</h2>
      {hint && <p className="text-xs text-slate-400 mb-5">{hint}</p>}
      {!hint && <div className="mb-5" />}
      {children}
    </section>
  );
}

function Field({ label, children, className = '' }) {
  const modern = React.useContext(ModernSkin);
  if (modern) {
    return (
      <div className={className}>
        <label className="block text-[12.5px] font-semibold mb-1.5" style={{ color: 'var(--color-heading, #1a2332)' }}>{label}</label>
        {children}
      </div>
    );
  }
  return (
    <div className={className}>
      <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">{label}</label>
      {children}
    </div>
  );
}

function ImageSlot({ index, url, fallback, disabled, uploading, onPick, onClear }) {
  const fileRef = useRef(null);
  const [adjustFile, setAdjustFile] = useState(null);
  return (
    <div className="relative aspect-[4/3] rounded-sm border border-slate-200 bg-slate-50 overflow-hidden group">
      {url ? (
        <>
          <img src={url} alt={`Slot ${index + 1}`} className="w-full h-full object-cover" />
          <button type="button" onClick={onClear}
            className="absolute top-1.5 right-1.5 p-1 rounded-full bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-opacity"
            title="Remove image"><X className="w-3.5 h-3.5" /></button>
        </>
      ) : (
        <button type="button" disabled={disabled || uploading} onClick={() => fileRef.current?.click()}
          className="w-full h-full flex flex-col items-center justify-center gap-1.5 text-slate-400 hover:text-[var(--color-accent,#0D9488)] disabled:opacity-40 disabled:cursor-not-allowed">
          {fallback && <img src={fallback} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />}
          {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
          <span className="text-[11px] relative">{index === 0 ? 'Main image' : `Image ${index + 1}`}</span>
        </button>
      )}
      <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp" className="hidden"
        onChange={e => { const f = e.target.files?.[0]; if (f) setAdjustFile(f); e.target.value = ''; }} />
      {adjustFile && (
        <ImageAdjust
          file={adjustFile}
          targetRatio={4 / 3}
          onCancel={() => setAdjustFile(null)}
          onConfirm={(blobOrFile, filename) => {
            setAdjustFile(null);
            const payload = (blobOrFile instanceof File) ? blobOrFile : new File([blobOrFile], filename, { type: blobOrFile.type });
            onPick(payload);
          }}
        />
      )}
    </div>
  );
}

export default function OpportunityEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id;

  const [form, setForm] = useState(null);
  const [types, setTypes] = useState([]);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploadingKind, setUploadingKind] = useState(null);

  const set = (patch) => setForm(f => ({ ...f, ...patch }));
  const modern = true; // s153: the expanded editor on every brand, skinned per theme

  useEffect(() => {
    opportunitiesAPI.types().then(r => setTypes(r.data)).catch(() => {});
    opportunitiesAPI.geoCountries().then(r => setCountries(r.data)).catch(() => {});
    if (isNew) setForm({ ...EMPTY });
    else opportunitiesAPI.get(id)
      .then(r => setForm({
        ...EMPTY, ...r.data,
        type_id: r.data.type_id ?? '',
        timer_hours: r.data.timer_hours ?? '',
        total_amount: r.data.total_amount ?? '',
        minimum_investment_amount: r.data.minimum_investment_amount ?? '',
        dates: { ...EMPTY.dates, ...(r.data.dates || {}) },
        socials: { ...EMPTY.socials, ...(r.data.socials || {}) },
        country_id: r.data.country_id || '', state_id: r.data.state_id || '', city_id: r.data.city_id || '',
        contact_email: r.data.contact_email || '', video_url: r.data.video_url || '',
      }))
      .catch(() => { toast.error('Opportunity not found'); navigate('/opportunities/develop'); });
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (form?.country_id) opportunitiesAPI.geoStates(form.country_id).then(r => setStates(r.data)).catch(() => setStates([]));
    else setStates([]);
  }, [form?.country_id]);
  useEffect(() => {
    if (form?.state_id) opportunitiesAPI.geoCities(form.state_id).then(r => setCities(r.data)).catch(() => setCities([]));
    else setCities([]);
  }, [form?.state_id]);

  const typeDefault = types.find(t => String(t.type_id) === String(form?.type_id))?.default_image || null;

  const payload = () => ({
    name: form.name, type_id: form.type_id === '' ? null : Number(form.type_id),
    country_id: form.country_id, state_id: form.state_id, city_id: form.city_id,
    contact_email: form.contact_email, video_url: form.video_url,
    dates: form.dates,
    total_amount: form.total_amount === '' ? null : form.total_amount,
    minimum_investment_amount: form.minimum_investment_amount === '' ? null : form.minimum_investment_amount,
    summary: form.summary, description: form.description,
    socials: form.socials,
    show_mode: form.show_mode,
    timer_hours: form.show_mode === 'timer' && form.timer_hours !== '' ? Number(form.timer_hours) : null,
    images: form.images, pfs_url: form.pfs_url,
    files: form.files, backers: form.backers, services: form.services,
    benefits: form.benefits, updates: form.updates, team: form.team,
    investment_instructions: (form.investment || {}).instructions || '',
    status: form.status === 'pending_review' ? undefined : form.status,
  });

  const save = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      const body = payload();
      if (body.status === undefined) delete body.status;
      if (isNew) {
        const r = await opportunitiesAPI.create(body);
        toast.success('Draft created');
        navigate(`/opportunities/develop/${r.data.id}`, { replace: true });
      } else {
        const r = await opportunitiesAPI.update(id, body);
        // Sync server-normalized rows back (new rows gained ids/timestamps).
        setForm(f => ({
          ...f, slug: r.data.slug,
          files: r.data.files, backers: r.data.backers, services: r.data.services,
          benefits: r.data.benefits, updates: r.data.updates, team: r.data.team,
        }));
        toast.success('Saved');
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete "${form.name}"?`)) return;
    try { await opportunitiesAPI.remove(id); toast.success('Deleted'); navigate('/opportunities/develop'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const publish = async () => {
    if (!window.confirm('Submit this opportunity for publication?')) return;
    setSaving(true);
    try {
      // Persist any pending edits first so the reviewed/published content
      // is exactly what is on screen.
      const body = payload();
      if (body.status === undefined) delete body.status;
      await opportunitiesAPI.update(id, body);
      const r = await opportunitiesAPI.publish(id);
      if (r.data.status === 'published') {
        toast.success('Published — it is now visible in the directory');
        setForm(f => ({ ...f, status: 'published' }));
      } else {
        toast.success('Submitted for review — it is published once the Opportunity Reviewers approve it');
        setForm(f => ({ ...f, status: 'pending_review' }));
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Publish failed'); }
    finally { setSaving(false); }
  };

  const upload = async (kind, file) => {
    setUploadingKind(kind);
    try {
      // s153: a new (unsaved) draft uploads to "new" and keeps the url in the
      // form; Create Draft then saves it with everything else (legacy add parity).
      const r = await opportunitiesAPI.upload(id || 'new', kind, file);
      const slot = /^image([1-5])$/.exec(kind);
      if (r.data.images) set({ images: r.data.images });
      else if (slot) setForm(f => ({ ...f, images: f.images.map((u, j) => (j === Number(slot[1]) - 1 ? r.data.url : u)) }));
      if (r.data.pfs_url) set({ pfs_url: r.data.pfs_url });
      else if (kind === 'pfs') set({ pfs_url: r.data.url });
      toast.success('Uploaded');
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    finally { setUploadingKind(null); }
  };

  if (!form) {
    return <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--color-background, #faf8f5)' }}>
      <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--color-accent, #0D9488)' }} />
    </div>;
  }

  const body = (
        <div className={modern ? 'flex flex-col gap-12' : 'space-y-6'}>
          <Section id="sec-basics" title="Basics">
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Opportunity Name *" className="md:col-span-2">
                <input className={inputCls} value={form.name} onChange={e => set({ name: e.target.value })} data-testid="opp-name" />
              </Field>
              <Field label="Type">
                <select className={inputCls} value={form.type_id} onChange={e => set({ type_id: e.target.value })} data-testid="opp-type">
                  <option value="">Select a type…</option>
                  {types.map(t => <option key={t.type_id} value={t.type_id}>{t.name}</option>)}
                </select>
              </Field>
              <Field label="Contact Email">
                <input type="email" className={inputCls} value={form.contact_email} onChange={e => set({ contact_email: e.target.value })} data-testid="opp-email" />
              </Field>
              <Field label="Country">
                <select className={inputCls} value={form.country_id} onChange={e => set({ country_id: e.target.value, state_id: '', city_id: '' })} data-testid="opp-country">
                  <option value="">Select…</option>
                  {countries.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </Field>
              <Field label="State / Province">
                <select className={inputCls} value={form.state_id} disabled={!form.country_id}
                  onChange={e => set({ state_id: e.target.value, city_id: '' })} data-testid="opp-state">
                  <option value="">Select…</option>
                  {states.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </Field>
              <Field label="City">
                <select className={inputCls} value={form.city_id} disabled={!form.state_id} onChange={e => set({ city_id: e.target.value })} data-testid="opp-city">
                  <option value="">Select…</option>
                  {cities.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </Field>
            </div>
          </Section>

          <Section id="sec-dates" title="Dates & Amounts">
            <div className="grid md:grid-cols-3 gap-4">
              {DATE_FIELDS.map(([k, label]) => (
                <Field key={k} label={label}>
                  <input type="date" className={inputCls} value={form.dates[k] || ''}
                    onChange={e => set({ dates: { ...form.dates, [k]: e.target.value } })} data-testid={`opp-date-${k}`} />
                </Field>
              ))}
            </div>
            <div className="grid md:grid-cols-2 gap-4 mt-4">
              <Field label="Total Amount (goal, USD)">
                <input type="number" min="0" step="any" className={inputCls} value={form.total_amount}
                  onChange={e => set({ total_amount: e.target.value })} data-testid="opp-total" />
              </Field>
              <Field label="Minimum Investment (USD)">
                <input type="number" min="0" step="any" className={inputCls} value={form.minimum_investment_amount}
                  onChange={e => set({ minimum_investment_amount: e.target.value })} data-testid="opp-min" />
              </Field>
            </div>
          </Section>

          <Section id="sec-content" title="Content">
            <div className="space-y-4">
              <Field label="Summary">
                <RichTextEditor value={form.summary} onChange={v => set({ summary: v })} placeholder="A short pitch shown on the directory card…" />
              </Field>
              <Field label="Description">
                <RichTextEditor value={form.description} onChange={v => set({ description: v })} placeholder="The full story: the project, the plan, the numbers…" />
              </Field>
              <Field label="Video URL">
                <input className={inputCls} value={form.video_url} onChange={e => set({ video_url: e.target.value })}
                  placeholder="https://youtube.com/…" data-testid="opp-video" />
              </Field>
            </div>
          </Section>

          <Section id="sec-media" title="Media" hint="Up to 5 images. The first slot is the main image.">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
              {form.images.map((url, i) => (
                <ImageSlot key={i} index={i} url={url} fallback={typeDefault} disabled={false}
                  uploading={uploadingKind === `image${i + 1}`}
                  onPick={file => upload(`image${i + 1}`, file)}
                  onClear={() => set({ images: form.images.map((u, j) => (j === i ? null : u)) })} />
              ))}
            </div>
            <Field label="Financial Statement (PFS)">
              {form.pfs_url ? (
                <div className="flex items-center gap-3 p-3 bg-slate-50 border border-slate-200 rounded-sm">
                  <FileText className="w-5 h-5 text-slate-400 shrink-0" />
                  <span className="text-sm text-slate-600 truncate flex-1">{form.pfs_url.split('/').pop()}</span>
                  <a href={form.pfs_url} target="_blank" rel="noreferrer" className="p-1.5 text-slate-400 hover:text-[var(--color-accent,#0D9488)]" title="View"><ExternalLink className="w-4 h-4" /></a>
                  <button type="button" onClick={() => set({ pfs_url: null })} className="p-1.5 text-slate-400 hover:text-red-500" title="Remove"><X className="w-4 h-4" /></button>
                </div>
              ) : (
                <label className={`inline-flex items-center gap-2 px-4 py-2.5 border border-dashed border-slate-300 rounded-sm text-sm text-slate-500 cursor-pointer hover:border-[var(--color-accent,#0D9488)] hover:text-[var(--color-accent,#0D9488)]`}>
                  {uploadingKind === 'pfs' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  Upload PDF / DOC / XLS / PPT
                  <input type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx" className="hidden"
                    onChange={e => { if (e.target.files?.[0]) upload('pfs', e.target.files[0]); e.target.value = ''; }} />
                </label>
              )}
            </Field>
          </Section>

          <Section id="sec-visibility" title="Visibility" hint="Choose how long the opportunity stays open for funding once published.">
            <div className="flex flex-col gap-3">
              <label className="inline-flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                <input type="radio" name="show_mode" checked={form.show_mode === 'all'}
                  onChange={() => set({ show_mode: 'all', timer_hours: '' })} className="accent-[var(--color-accent,#0D9488)]" data-testid="opp-mode-all" />
                Show to all — open until the Funding End Date
              </label>
              <label className="inline-flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                <input type="radio" name="show_mode" checked={form.show_mode === 'timer'}
                  onChange={() => set({ show_mode: 'timer' })} className="accent-[var(--color-accent,#0D9488)]" data-testid="opp-mode-timer" />
                For a limited time — countdown timer from publication
              </label>
              {form.show_mode === 'timer' && (
                <div className="ml-6 max-w-xs">
                  <select className={inputCls} value={form.timer_hours} onChange={e => set({ timer_hours: e.target.value })} data-testid="opp-timer-hours">
                    <option value="">Select duration…</option>
                    {TIMER_OPTIONS.map(h => <option key={h} value={h}>{h} hours</option>)}
                  </select>
                </div>
              )}
            </div>
          </Section>

          {SUB_GROUPS.map(g => (
            <div key={g} id={`sec-${g}`}>
              <RepeatableGroup group={g} oppId={id || 'new'} disabled={false}
                rows={form[g]} onChange={rows => set({ [g]: rows })} />
            </div>
          ))}

          <div id="sec-faq">
            <FaqManager oppId={id} faq={form.faq} disabled={isNew} onCreateDraft={isNew ? save : undefined}
              onChange={faq => set({ faq })} />
          </div>

          <div id="sec-team">
            <TeamPicker team={form.team} disabled={false}
              onChange={team => set({ team })} />
          </div>

          <Section id="sec-fundit" title="Fund it" hint="Investors commit an amount and receive a commitment letter with your bank details (PDF) by email.">
            <FundItEditor id={id} form={form} set={set} isNew={isNew} />
          </Section>

          <Section id="sec-social" title="Social Links">
            <div className="grid md:grid-cols-2 gap-4">
              {SOCIAL_FIELDS.map(k => (
                <Field key={k} label={k.charAt(0).toUpperCase() + k.slice(1)}>
                  <input className={inputCls} value={form.socials[k] || ''} placeholder="https://…"
                    onChange={e => set({ socials: { ...form.socials, [k]: e.target.value } })} data-testid={`opp-social-${k}`} />
                </Field>
              ))}
            </div>
          </Section>

          {!modern && <div className="bg-white rounded-sm border border-slate-100 p-6 flex flex-wrap items-center gap-4 sticky bottom-4 shadow-[0_4px_24px_rgba(26,35,50,0.08)]">
            {form.status === 'published' ? (
              <span className="text-xs px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-600 font-medium">Published</span>
            ) : form.status === 'pending_review' ? (
              <span className="text-xs px-3 py-1.5 rounded-full bg-amber-50 text-amber-600 font-medium">Pending peer review</span>
            ) : (
              <Field label="Status" className="w-44">
                <select className={inputCls} value={form.status}
                  onChange={e => set({ status: e.target.value })} data-testid="opp-status">
                  <option value="draft">Draft</option>
                  <option value="archived">Archive</option>
                </select>
              </Field>
            )}
            <div className="flex-1" />
            {!isNew && (form.status === 'draft' || form.status === 'archived') && (
              <button onClick={remove} className="inline-flex items-center gap-1.5 px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 rounded-sm" data-testid="opp-delete-btn">
                <Trash2 className="w-4 h-4" /> Delete
              </button>
            )}
            <Link to="/opportunities/develop" className="px-4 py-2.5 text-sm text-slate-500 hover:text-slate-700">Cancel</Link>
            {!isNew && (form.status === 'draft' || form.status === 'archived') && (
              <button onClick={publish} disabled={saving}
                className="inline-flex items-center gap-1.5 px-5 py-2.5 text-sm font-medium rounded-sm border disabled:opacity-50"
                style={{ borderColor: 'var(--color-accent, #0D9488)', color: 'var(--color-accent, #0D9488)' }}
                data-testid="opp-publish-btn">
                <Send className="w-4 h-4" /> Submit for Publication
              </button>
            )}
            <button onClick={save} disabled={saving}
              className="px-6 py-2.5 rounded-sm text-sm font-medium disabled:opacity-50 inline-flex items-center gap-2"
              style={{ backgroundColor: 'var(--color-button-bg, #1a2332)', color: 'var(--color-button-text, #fff)' }}
              data-testid="opp-save-btn">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />} {isNew ? 'Create Draft' : 'Save'}
            </button>
          </div>}
        </div>
  );

  if (modern) {
    return (
      <ModernSkin.Provider value>
        <ModernEditorShell form={form} isNew={isNew} saving={saving} set={set}
          onSave={save} onPublish={publish} onRemove={remove}>
          {body}
        </ModernEditorShell>
      </ModernSkin.Provider>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--color-background, #faf8f5)', fontFamily: PB_FONT }}>
      <div className="max-w-4xl mx-auto px-6 md:px-12 py-16 pt-24 md:pt-28">
        <Link to="/opportunities/develop" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[var(--color-accent,#0D9488)] mb-6">
          <ChevronLeft className="w-4 h-4" /> My Opportunities
        </Link>
        <div className="mb-8">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold mb-3" style={{ color: 'var(--color-accent, #0D9488)' }}>
            Opportunity Development
          </div>
          <h1 className="text-3xl font-bold" style={{ color: 'var(--color-heading, #1a2332)', fontFamily: 'Playfair Display, serif' }}>
            {isNew ? 'New Opportunity' : (form.name || '(untitled)')}
          </h1>
        </div>
        {body}
      </div>
    </div>
  );
}

/* ── «Moderno» editor shell: page header, section rail, graphite action bar ── */
const plainText = (h) => String(h || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();

function railSections(form, isNew) {
  const n = (rows) => (rows || []).length;
  const faqCount = (form.faq || []).reduce((a, t) => a + (t.questions || []).length, 0);
  const photos = (form.images || []).filter(Boolean).length;
  const socials = Object.values(form.socials || {}).filter(Boolean).length;
  return [
    { id: 'sec-basics', label: 'Basics', done: !!(form.name || '').trim() }, // the name is the only required field
    { id: 'sec-dates', label: 'Dates & amounts', done: !!(Object.values(form.dates || {}).some(Boolean) && form.total_amount !== '') },
    { id: 'sec-content', label: 'Content', done: !!(plainText(form.summary) || plainText(form.description)) },
    { id: 'sec-media', label: 'Media', done: photos > 0, count: photos ? `${photos} photo${photos === 1 ? '' : 's'}` : '' },
    // 'Show to all' is the default: only counts once saved (or a timer is picked)
    { id: 'sec-visibility', label: 'Visibility', done: form.show_mode === 'timer' ? !!form.timer_hours : !isNew },
    { id: 'sec-files', label: 'Files', done: n(form.files) > 0, count: n(form.files) || '' },
    { id: 'sec-backers', label: 'Backer pledges', done: n(form.backers) > 0, count: n(form.backers) || '' },
    { id: 'sec-services', label: 'Services', done: n(form.services) > 0, count: n(form.services) || '' },
    { id: 'sec-benefits', label: 'Investment benefits', done: n(form.benefits) > 0, count: n(form.benefits) || '' },
    { id: 'sec-updates', label: 'Updates', done: n(form.updates) > 0, count: n(form.updates) || '' },
    { id: 'sec-faq', label: 'FAQ', done: faqCount > 0, count: faqCount || '' },
    { id: 'sec-team', label: 'Team', done: n(form.team) > 0, count: n(form.team) || '' },
    { id: 'sec-fundit', label: 'Fund it', done: !!(form.investment || {}).bank_pdf },
    { id: 'sec-social', label: 'Social links', done: socials > 0, count: socials || '' },
  ];
}

function ModernEditorShell({ form, isNew, saving, set, onSave, onPublish, onRemove, children }) {
  const secs = railSections(form, isNew);
  const [active, setActive] = useState('sec-basics');
  const lockUntil = useRef(0); // after a rail click, ignore the observer while the page scrolls
  const clicked = useRef(null); // sections near the end cannot reach the top: keep the clicked one
  const doneCount = secs.filter(s => s.done).length;
  const shell = 'max-w-[1440px] mx-auto px-5 sm:px-6 lg:px-[clamp(24px,5vw,80px)]';
  const editable = form.status === 'draft' || form.status === 'archived';

  // Underline the section in view (the last one whose top passed the navbar).
  useEffect(() => {
    const els = secs.map(s => document.getElementById(s.id)).filter(Boolean);
    if (!els.length) return undefined;
    const io = new IntersectionObserver(() => {
      if (Date.now() < lockUntil.current) return;
      const atEnd = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 4;
      if (atEnd && clicked.current) { setActive(clicked.current); return; }
      clicked.current = null;
      const passed = els.filter(el => el.getBoundingClientRect().top <= NAV_H + 60);
      setActive((passed[passed.length - 1] || els[0]).id);
    }, { rootMargin: `-${NAV_H + 40}px 0px -55% 0px`, threshold: [0, 1] });
    els.forEach(el => io.observe(el));
    return () => io.disconnect();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const go = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    setActive(id);
    clicked.current = id;
    lockUntil.current = Date.now() + (reduce ? 0 : 1200);
    window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - NAV_H - 24, behavior: reduce ? 'auto' : 'smooth' });
  };

  return (
    <div className="moe bg-white min-h-screen" style={opportunitySkin(useSettings().active_theme)} data-testid="opportunity-editor">
      <style>{MOE_CSS}</style>
      <header className={`${shell} pt-28 md:pt-32 pb-6 border-b flex flex-col gap-3`} style={{ borderColor: 'rgba(20,26,58,.1)' }}>
        <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-2 text-[13px] font-semibold" style={{ color: FAINT }}>
          <Link to="/" className="mx-link" style={{ color: 'var(--color-heading, #1a2332)' }}>Home</Link><span aria-hidden="true">/</span>
          <Link to="/opportunities" className="mx-link" style={{ color: 'var(--color-heading, #1a2332)' }}>Opportunities</Link><span aria-hidden="true">/</span>
          <Link to="/opportunities/develop" className="mx-link" style={{ color: 'var(--color-heading, #1a2332)' }}>Develop</Link><span aria-hidden="true">/</span>
          <span className="truncate max-w-[40vw]">{isNew ? 'New' : (form.name || '(untitled)')}</span>
        </nav>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <h1 className="font-bold max-w-[28ch]" style={{ fontFamily: "var(--op-head, 'Playfair Display', Georgia, serif)", fontSize: 'clamp(1.9rem, 3.4vw, 2.7rem)', lineHeight: 1.08, color: 'var(--color-heading, #1a2332)', textWrap: 'balance' }}>
            {isNew ? 'New Opportunity' : (form.name || '(untitled)')}
          </h1>
          {!isNew && (
            <div className="flex items-center gap-4">
              <StatusChip status={form.status} />
              {form.status === 'published' && form.slug && (
                <Link to={`/opportunities/${form.slug}`} className="mx-link inline-flex items-center gap-1.5 text-[13px] font-bold" style={{ color: 'var(--op-strong, #b86500)' }}>
                  View public page <ExternalLink className="w-3.5 h-3.5" />
                </Link>
              )}
            </div>
          )}
        </div>
      </header>

      <div className={`${shell} py-10 grid gap-12 lg:grid-cols-[240px_minmax(0,1fr)] items-start`}>
        <aside className="hidden lg:block sticky" style={{ top: NAV_H + 24 }} aria-label="Sections">
          <div className="flex justify-between pb-2.5 border-b text-[11px] font-bold uppercase" style={{ letterSpacing: '.1em', color: FAINT, borderColor: 'rgba(20,26,58,.1)' }}>
            Sections <span className="tabular-nums">{doneCount} / {secs.length}</span>
          </div>
          <ol className="pt-1.5">
            {secs.map(s => (
              <li key={s.id}>
                <button type="button" onClick={() => go(s.id)} aria-current={active === s.id ? 'true' : undefined}
                  className="w-full flex items-center gap-2.5 px-2.5 -mx-2.5 py-2 rounded-[var(--op-radius,3px)] text-left text-sm font-semibold transition-colors duration-150 hover:bg-[rgba(20,26,58,.04)]"
                  style={{ color: 'var(--color-heading, #1a2332)', ...(active === s.id ? { background: 'rgba(20,26,58,.05)', boxShadow: 'inset 2px 0 0 var(--color-accent, #ff9c1b)' } : {}) }}
                  data-testid={`rail-${s.id}`}>
                  <span className="w-[18px] h-[18px] rounded-full grid place-items-center flex-none border-[1.5px]"
                    style={s.done ? { background: 'var(--color-accent, #ff9c1b)', borderColor: 'var(--color-accent, #ff9c1b)', color: 'var(--accent-ink, #16171a)' } : { borderColor: 'rgba(20,26,58,.25)' }}>
                    {s.done && <Check className="w-3 h-3" strokeWidth={3} />}
                  </span>
                  <span className="flex-1">{s.label}</span>
                  {s.count !== '' && s.count != null && <span className="text-xs font-semibold tabular-nums" style={{ color: FAINT }}>{s.count}</span>}
                </button>
              </li>
            ))}
          </ol>
        </aside>
        <div className="min-w-0 max-w-[920px]">
          {children}
          {/* Action bar: white card at the form's width, sticky so it never blends into the dark footer */}
          <div className="sticky bottom-3 sm:bottom-4 z-20 mt-10 bg-white border rounded-[var(--op-radius,3px)] shadow-[0_6px_28px_rgba(20,26,58,.12)]"
            style={{ borderColor: 'rgba(20,26,58,.12)' }} data-testid="opportunity-action-bar">
            <div className="px-4 sm:px-5 py-3 flex flex-wrap items-center gap-x-3 gap-y-2" style={{ color: 'var(--color-heading, #1a2332)' }}>
              {form.status === 'published' || form.status === 'pending_review' ? (
                <StatusChip status={form.status} />
              ) : (
                <label className="flex items-center gap-2.5 text-[13px]">
                  <span style={{ color: FAINT }}>Status</span>
                  <select value={form.status} onChange={e => set({ status: e.target.value })} data-testid="opp-status"
                    className="h-11 rounded-[var(--op-radius,3px)] border bg-white px-3 text-sm font-semibold" style={{ borderColor: 'rgba(20,26,58,.2)' }}>
                    <option value="draft">Draft</option>
                    <option value="archived">Archive</option>
                  </select>
                </label>
              )}
              <span className="flex-1" />
              <div className="flex flex-wrap items-center justify-end gap-2 sm:gap-3 w-full sm:w-auto">
                {!isNew && editable && (
                  <button type="button" onClick={onRemove} className="inline-flex items-center gap-1.5 min-h-[44px] px-3 rounded-full text-sm font-semibold text-red-600 hover:text-red-700" data-testid="opp-delete-btn">
                    <Trash2 className="w-4 h-4" /> Delete
                  </button>
                )}
                <Link to="/opportunities/develop" className="mx-link inline-flex items-center min-h-[44px] px-3 text-sm font-semibold" style={{ color: 'var(--color-heading, #1a2332)' }}>Cancel</Link>
                {!isNew && editable && (
                  <button type="button" onClick={onPublish} disabled={saving}
                    className="mx-btn mx-btn--secondary inline-flex items-center gap-2 min-h-[44px] px-5 rounded-full border text-sm font-bold disabled:opacity-50"
                    style={{ borderColor: 'rgba(20,26,58,.3)', color: 'var(--color-heading, #1a2332)' }}
                    data-testid="opp-publish-btn">
                    <Send className="w-4 h-4" /> Submit for Publication
                  </button>
                )}
                <button type="button" onClick={onSave} disabled={saving}
                  className="mx-btn mx-btn--primary inline-flex items-center gap-2 min-h-[44px] px-6 rounded-full text-sm font-bold disabled:opacity-50"
                  style={{ background: 'var(--color-accent, #ff9c1b)', color: 'var(--accent-ink, #16171a)' }} data-testid="opp-save-btn">
                  {saving && <Loader2 className="w-4 h-4 animate-spin" />} {isNew ? 'Create Draft' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
